import { AxiosRequestConfig } from 'axios';
import { ResponseError } from 'services/api/base';
import { ContractCsvParams } from 'helper/companies';
import { ensureFreshProductKey, resolveProductKey } from 'services/api/productKeyRecovery';
import { GetZipFileParams, buildZipRequestBody } from 'helper/products/flux';
import { ampereFileApi, prepareResponse } from './base';
import { prepareBulletinResponse } from './bulletin';

// Downloads via axios não passam pelo baseQuery, então a recuperação
// silenciosa do 403 de product_key (issue #1841) é feita aqui: chave resolvida
// antes de montar a URL (nunca a sentinela) e uma repetição após 403.
const ehErro403DeChave = (resultado: Blob | ResponseError): resultado is ResponseError => (
  !(resultado instanceof Blob)
  && resultado.status === 403
  && resultado.data !== 'JWT Inválido'
);

const comRecuperacaoDeChave = async (
  produto: string,
  baixar: (productKey: string) => Promise<Blob | ResponseError>,
): Promise<Blob | ResponseError> => {
  const chave = await resolveProductKey(produto);
  const resultado = await baixar(chave);
  if (!ehErro403DeChave(resultado)) return resultado;

  const chaveNova = await ensureFreshProductKey(produto, chave || null);
  if (!chaveNova || chaveNova === chave) return resultado;

  return baixar(chaveNova);
};

// eslint-disable-next-line import/prefer-default-export
export async function downloadCompanyListCsvBlob(
  params: ContractCsvParams,
): Promise<Blob | ResponseError> {
  const response = await ampereFileApi().post('contratos-empresa-list-csv', params);

  return prepareResponse(response);
}

export async function downloadContractBlob(
  params: number,
): Promise<Blob | ResponseError> {
  const response = await ampereFileApi().get(`admin/contratos/download-file/?id_anexo=${params}`);

  return prepareResponse(response);
}

// `onDownloadProgress` alimenta a % do card de download e `signal` permite cancelar (#1956).
export type ZipDownloadConfig = Pick<AxiosRequestConfig, 'onDownloadProgress' | 'signal'>;

export async function downloadFaDailyZip(params: GetZipFileParams, config?: ZipDownloadConfig) {
  return comRecuperacaoDeChave('prevs-automatico', async (productKey) => {
    const url = `produtos/previvaz-ena-diaria/get-zip/?product_key=${productKey}`;
    const response = await ampereFileApi().post(url, buildZipRequestBody(params), config);
    return prepareResponse(response);
  });
}

/**
 * Boletim (PDF) fora do RTK Query.
 *
 * A rota `download-boletim/` responde JSON com o PDF em `data.file_b64`. Passar isso pelo
 * `fetchBaseQuery` significava um `res.json()` sobre ~26,1 MB (base64 infla 33%): se a
 * transferência fosse cortada no meio do stream, o parse estourava, virava PARSING_ERROR e o
 * usuário perdia a página inteira por causa de um download.
 *
 * O download vem por axios, mas o corpo continua sendo JSON — quem sabe desembrulhar é o
 * `prepareBulletinResponse`. Usar o `prepareResponse` genérico aqui foi o bug de produção de
 * 13/08/2026: o envelope JSON era salvo como se fosse o PDF.
 */
export async function downloadBulletinBlob({ id, productPath, permissionType }: {
  id: number,
  productPath: string,
  permissionType: string,
}): Promise<Blob | ResponseError> {
  return comRecuperacaoDeChave(permissionType, async (productKey) => {
    const url = `produtos/media/${productPath}/download-boletim/?product_key=${productKey}`;
    const response = await ampereFileApi().post(url, { id });

    return prepareBulletinResponse(response);
  });
}

export async function downloadFileViewerFile(fileId: string): Promise<Blob | ResponseError> {
  return comRecuperacaoDeChave('file-viewer', async (productKey) => {
    const url = `/produtos/file-viewer/get-file/?product_key=${productKey}&file-id=${fileId}`;
    const response = await ampereFileApi().get(url);

    return prepareResponse(response);
  });
}
